/**
 * API Client Service
 * Handles data fetching from static JSON configuration
 * No backend server required - all data loaded from public/data/
 */

/**
 * Static data paths
 */
const POSTERS_JSON_URL = '/data/posters.json';

/**
 * In-memory cache for posters data
 */
let postersCache = null;

/**
 * API Client class for static JSON configuration
 */
export class ApiClient {
  constructor() {
    this.postersData = null;
  }

  /**
   * Load posters configuration from static JSON
   * @returns {Promise<object>} - Posters data object
   */
  async loadPostersData() {
    if (postersCache) {
      return postersCache;
    }

    try {
      const response = await fetch(POSTERS_JSON_URL);
      if (!response.ok) {
        throw new Error(`Failed to load posters configuration: ${response.status}`);
      }

      postersCache = await response.json();
      return postersCache;
    } catch (error) {
      console.error('Failed to load posters.json:', error);
      throw new Error('Failed to load application configuration. Please check your setup.');
    }
  }

  /**
   * Fetch poster data by ID
   * @param {string} posterId - Poster ID
   * @returns {Promise<object>} - Poster data with videoId, videoUrl, targetUrl
   */
  async fetchPoster(posterId) {
    const posters = await this.loadPostersData();

    const poster = posters[posterId];
    if (!poster) {
      const error = new Error(`Poster not found: ${posterId}`);
      error.status = 404;
      throw error;
    }

    return poster;
  }

  /**
   * Fetch video metadata by ID
   * @param {string} videoId - Video ID
   * @returns {Promise<object>} - Video metadata with url
   */
  async fetchVideo(videoId) {
    // Find poster with this videoId to get the video URL
    const posters = await this.loadPostersData();

    for (const poster of Object.values(posters)) {
      if (poster.videoId === videoId) {
        return {
          id: videoId,
          url: poster.videoUrl,
          name: poster.name,
        };
      }
    }

    const error = new Error(`Video not found: ${videoId}`);
    error.status = 404;
    throw error;
  }

  /**
   * Create new AR session (no-op for static configuration)
   * @param {string} posterId - Poster ID
   * @returns {Promise<object>} - Session data with sessionId
   */
  async createSession(posterId) {
    // Generate client-side session ID
    const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
      sessionId,
      posterId,
      createdAt: new Date().toISOString(),
    };
  }
}

/**
 * Default API client instance
 */
export const apiClient = new ApiClient();
